import React from 'react';
import { Wrench, Landmark, Building2, Scale, Home, Network, ShieldCheck, Phone, MapPin } from 'lucide-react';
import { networkPartners, networkPrinciples } from '../data/mock';

const iconMap = {
  Wrench: Wrench,
  Landmark: Landmark,
  Building2: Building2,
  Scale: Scale,
  Home: Home,
  ShieldCheck: ShieldCheck,
  Phone: Phone,
  MapPin: MapPin,
  Network: Network
};

export const NetworkPartners = () => {
  return (
    <section
      id="netzwerk"
      data-testid="network-section"
      className="relative py-24 md:py-28 bg-gradient-to-b from-atob-blue via-atob-blue to-[#0A1420] overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-atob-orange/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
          <span className="inline-flex items-center gap-2 text-atob-orange font-semibold text-sm uppercase tracking-[0.2em] mb-5">
            <span className="h-px w-8 bg-atob-orange"></span>
            Unser stärkster Hebel
            <span className="h-px w-8 bg-atob-orange"></span>
          </span>
          <h2
            data-testid="network-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-6"
          >
            Das Netzwerk hinter{' '}
            <span className="text-atob-orange italic font-serif">jeder Antwort</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Immobilien sind ein Teamsport. Wir haben über Jahre die Partner zusammengebracht,
            die wirklich liefern – in fünf zentralen Disziplinen.
          </p>
        </div>

        {/* Network Categories Grid */}
        <div
          data-testid="network-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6 max-w-7xl mx-auto mb-20 md:mb-24"
        >
          {networkPartners.map((partner, idx) => {
            const Icon = iconMap[partner.icon] || Network;
            return (
              <div
                key={partner.id}
                data-testid={`network-card-${partner.id}`}
                className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-7 transition-all duration-500 hover:bg-white/[0.06] hover:border-atob-orange/40 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-15px_rgba(245,166,35,0.35)] flex flex-col"
                style={{ animation: `fadeInUp 0.5s ease-out ${idx * 0.1}s both` }}
              >
                <div className="relative mb-5 mx-auto">
                  <div className="w-16 h-16 rounded-2xl bg-atob-orange/15 border border-atob-orange/30 flex items-center justify-center group-hover:bg-atob-orange/25 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <Icon className="h-8 w-8 text-atob-orange" strokeWidth={1.75} />
                  </div>
                  {/* Number badge */}
                  <span className="absolute -top-2 -right-2 bg-atob-orange text-atob-blue text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center shadow-md">
                    {String(partner.id).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-2 text-center group-hover:text-atob-orange transition-colors duration-300">
                  {partner.category}
                </h3>
                <p className="text-sm text-gray-300 text-center leading-relaxed">
                  {partner.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Principles - why our network */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Warum unser Netzwerk anders funktioniert
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Vier Prinzipien, die Ihre Interessen an erste Stelle setzen.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {networkPrinciples.map((principle, idx) => {
              const Icon = iconMap[principle.icon] || ShieldCheck;
              return (
                <div
                  key={principle.id}
                  data-testid={`principle-${principle.id}`}
                  className="flex flex-col items-start gap-4 p-6 border-l-2 border-atob-orange/40 hover:border-atob-orange transition-colors duration-300"
                  style={{ animation: `fadeInUp 0.5s ease-out ${(idx + 5) * 0.08}s both` }}
                >
                  <Icon className="h-6 w-6 text-atob-orange" strokeWidth={2} />
                  <h4 className="font-bold text-white text-lg leading-tight">{principle.title}</h4>
                  <p className="text-sm text-gray-300 leading-relaxed">{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
